import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { UserRoundPlus } from 'lucide-react';
import type { SceneStore, TaskItem } from '@os/doc';
import { avatarPhoto } from '../../lib/avatar';
import { Avatar } from '../../ui/Avatar';
import { Menu } from '../../ui/Menu';
import { CanvasPeopleContext, type CanvasPeople } from '../editor/CanvasAuthors';
import { ExportModeContext } from '../editor/embeds';

/** Disc colour of someone whose colour is unknown (yourself, a former member). */
export const PERSON_FALLBACK = '#71717a';

/**
 * A task's assignee (P16-5): their avatar, or a faint "assign" button on hover; a menu of the board's people and
 * "Nobody". Absent on a local board (nobody to assign), and in an export when nobody is assigned.
 */
export function TaskAssign({ store, listId, item, disabled }: { store: SceneStore; listId: string; item: TaskItem; disabled: boolean }) {
  const people = useContext(CanvasPeopleContext);
  const exporting = useContext(ExportModeContext);
  if (!people || (exporting && !item.assignee)) return null;
  return <Assign people={people} store={store} listId={listId} item={item} disabled={disabled} />;
}

function Assign({ people, store, listId, item, disabled }: { people: CanvasPeople; store: SceneStore; listId: string; item: TaskItem; disabled: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const list = useQuery({ queryKey: ['taskPeople', people.boardId], queryFn: () => people.people!(), enabled: open && !!people.people, staleTime: 60_000 });
  const who = item.assignee ? people.person(item.assignee) : null;
  const assign = (userId: string | null) => store.commands.taskAssign(listId, item.id, userId);
  return (
    <Menu.Root open={open} onOpenChange={setOpen}>
      <Menu.Trigger
        data-assignee=""
        data-canvas-ignore=""
        disabled={disabled}
        aria-label={who ? t('tasks.assignedTo', { name: who.you ? t('authors.you') : who.name }) : t('tasks.assign')}
        className={`flex size-6 shrink-0 items-center justify-center rounded-full text-[#a1a1aa] hover:text-[#141414] disabled:cursor-default pointer-coarse:size-11 ${who || item.assignee ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100'}`}
      >
        {item.assignee ? <Avatar name={who?.name ?? ''} color={who?.color ?? PERSON_FALLBACK} photo={who?.photo} size="sm" /> : <UserRoundPlus size={14} strokeWidth={2} aria-hidden="true" />}
      </Menu.Trigger>
      <Menu.Popup label={t('tasks.assign')} side="bottom" align="end">
        {(list.data ?? []).map((p) => (
          <Menu.Item key={p.userId} onClick={() => assign(p.userId)} className="gap-2">
            <span aria-hidden="true">
              <Avatar name={p.displayName} color={p.color ?? PERSON_FALLBACK} photo={avatarPhoto(p.avatarKey, p.userId)} size="sm" />
            </span>
            {p.displayName}
          </Menu.Item>
        ))}
        {list.isLoading && <Menu.Item disabled>{t('tasks.loadingPeople')}</Menu.Item>}
        <Menu.Separator />
        <Menu.Item onClick={() => assign(null)}>{t('tasks.nobody')}</Menu.Item>
      </Menu.Popup>
    </Menu.Root>
  );
}
